import React, { useEffect, useState } from "react";
import { Modal, Button, Input } from "./common";
import {
  planilhaService,
  type PlanilhaDuplicada,
} from "../services/planilhaService";
import { Planilha } from "../types";
import { UF_ESTADOS } from "./EditarPlanilhaModal";
import {
  WRIKE_PIT_ERRO_FORMATO,
  WRIKE_PIT_PLACEHOLDER,
  linkPitValido,
} from "../utils/wrikeLink";
import {
  HiDuplicate,
  HiX,
  HiExclamation,
  HiInformationCircle,
} from "react-icons/hi";

interface DuplicarPlanilhaModalProps {
  planilha: Planilha | null;
  clientes: Array<{ id: string; nome: string }>;
  isOpen: boolean;
  onClose: () => void;
  onDuplicated: (nova: PlanilhaDuplicada) => void;
  onError: (message: string) => void;
}

interface FormState {
  clienteId: string;
  evento: string;
  dataInicio: string;
  dataFim: string;
  localEvento: string;
  ufEvento: string;
  linkProjeto: string;
}

const emptyForm: FormState = {
  clienteId: "",
  evento: "",
  dataInicio: "",
  dataFim: "",
  localEvento: "",
  ufEvento: "",
  linkProjeto: "",
};

export const DuplicarPlanilhaModal: React.FC<DuplicarPlanilhaModalProps> = ({
  planilha,
  clientes,
  isOpen,
  onClose,
  onDuplicated,
  onError,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // O modal é uma tela de conferência: chega tudo preenchido com os dados da
  // origem e a pessoa ajusta o que mudou. A exceção é o Link PIT, que fica em
  // branco de propósito — herdá-lo apontaria a cópia para o projeto antigo.
  useEffect(() => {
    if (isOpen && planilha) {
      setForm({
        clienteId: planilha.clienteId ?? "",
        evento: planilha.evento ?? "",
        dataInicio: planilha.dataInicio ?? "",
        dataFim: planilha.dataFim ?? "",
        localEvento: planilha.localEvento ?? "",
        ufEvento: planilha.ufEvento ?? "",
        linkProjeto: "",
      });
      setError(null);
    }
  }, [isOpen, planilha]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Valida o valor aparado, que é o que segue no payload.
  const linkComFormatoInvalido =
    !!form.linkProjeto.trim() && !linkPitValido(form.linkProjeto.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!planilha) return;

    if (!form.clienteId) {
      setError("Selecione o cliente.");
      return;
    }
    if (!form.evento.trim()) {
      setError("O campo Evento/Ação é obrigatório.");
      return;
    }
    if (!form.dataInicio) {
      setError("A Data de Início é obrigatória.");
      return;
    }
    if (form.dataFim && form.dataFim < form.dataInicio) {
      setError("A Data de Fim não pode ser anterior à Data de Início.");
      return;
    }
    if (linkComFormatoInvalido) {
      setError(WRIKE_PIT_ERRO_FORMATO);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const nova = await planilhaService.duplicar(planilha.id, {
        clienteId: form.clienteId,
        evento: form.evento.trim(),
        dataInicio: form.dataInicio,
        dataFim: form.dataFim || null,
        localEvento: form.localEvento.trim() || null,
        ufEvento: form.ufEvento || null,
        linkProjeto: form.linkProjeto.trim() || null,
      });

      onDuplicated(nova);
      onClose();
    } catch (err: any) {
      console.error(err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Não foi possível duplicar a planilha.";
      setError(msg);
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicar Planilha"
      size="large"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            <HiX className="h-4 w-4" />
            Cancelar
          </Button>
          <Button
            type="submit"
            form="duplicar-planilha-form"
            variant="success"
            loading={submitting}
            disabled={submitting}
          >
            <HiDuplicate className="h-4 w-4" />
            Duplicar
          </Button>
        </>
      }
    >
      <form
        id="duplicar-planilha-form"
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {error && (
          <div className="alert alert-error text-sm py-2">
            <span>{error}</span>
          </div>
        )}

        {/* O que a cópia leva e o que não leva, mais o aviso de conferir os
            campos — junto, num lugar só, em vez de repetido em cada helper
            text. Menos altura e a regra fica legível de uma vez. */}
        <div className="alert alert-info text-sm py-3 items-start">
          <HiInformationCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p>
              A cópia traz os itens e os fornecedores de{" "}
              <strong>{planilha?.evento}</strong> — produto, descritivo,
              quantidade e diária. Valores cotados e propostas{" "}
              <strong>não são copiados</strong>: cada linha nasce pronta para
              pedir cotação de novo.
            </p>
            <p className="opacity-80">
              Os campos abaixo vieram da planilha de origem.{" "}
              <strong>Confira as datas</strong> antes de duplicar.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
          <div className="form-control w-full">
            <label className="label" htmlFor="duplicar-cliente">
              <span className="label-text font-medium text-base-content">
                Cliente *
              </span>
            </label>
            <select
              id="duplicar-cliente"
              name="clienteId"
              value={form.clienteId}
              onChange={handleChange}
              className="select select-bordered w-full"
              required
            >
              <option value="">Selecione o cliente</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Evento/Ação *"
            type="text"
            name="evento"
            value={form.evento}
            onChange={handleChange}
            required
            placeholder="Ex: ASSINATURA PROJETOS SOCIAIS"
            fullWidth
          />

          <Input
            label="Data de Início *"
            type="date"
            name="dataInicio"
            value={form.dataInicio}
            onChange={handleChange}
            required
            fullWidth
          />

          <Input
            label="Data de Fim"
            type="date"
            name="dataFim"
            value={form.dataFim}
            onChange={handleChange}
            fullWidth
          />

          <Input
            label="Cidade do Evento"
            type="text"
            name="localEvento"
            value={form.localEvento}
            onChange={handleChange}
            placeholder="Ex: São Paulo"
            fullWidth
          />

          <div className="form-control w-full">
            <label className="label" htmlFor="duplicar-uf">
              <span className="label-text font-medium text-base-content">
                UF do Evento
              </span>
            </label>
            <select
              id="duplicar-uf"
              name="ufEvento"
              value={form.ufEvento}
              onChange={handleChange}
              className="select select-bordered w-full"
            >
              <option value="">Selecione a UF</option>
              {UF_ESTADOS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Aviso e campo no mesmo bloco: o alerta existe por causa deste input,
            e separá-los fazia o usuário ler a regra longe de onde ela age. */}
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 space-y-3">
          <div className="flex gap-2 text-sm">
            <HiExclamation className="h-5 w-5 shrink-0 text-warning" />
            <span>
              Nada do Wrike é copiado: <strong>PIT</strong>,{" "}
              <strong>Nº OC</strong> e as tarefas são da planilha de origem, e a
              cópia gera as dela. O Nº OC aparece quando você enviar a cópia
              para o Wrike.
            </span>
          </div>

          <Input
            label="Link PIT do novo projeto"
            type="url"
            name="linkProjeto"
            value={form.linkProjeto}
            onChange={handleChange}
            placeholder={WRIKE_PIT_PLACEHOLDER}
            fullWidth
            error={linkComFormatoInvalido ? WRIKE_PIT_ERRO_FORMATO : undefined}
            helperText={
              linkComFormatoInvalido
                ? undefined
                : "O PIT é extraído deste link. Pode ficar em branco e ser preenchido depois, em Editar."
            }
          />
        </div>
      </form>
    </Modal>
  );
};

export default DuplicarPlanilhaModal;
